/**
 * Ban User Form Component
 */

import { useState } from 'react';
import { banUser } from '../../../services/moderation';

export function BanUserForm() {
  const [alias, setAlias] = useState('');
  const [topicId, setTopicId] = useState('');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!alias || !reason) {
      setMessage({ type: 'error', text: 'Alias and reason are required' });
      return;
    }

    setIsSubmitting(true);

    try {
      await banUser({
        userId: alias, // Will be interpreted as alias by backend
        topicId: isGlobal ? undefined : topicId || undefined,
        reason,
        durationMinutes: duration ? parseInt(duration) : undefined,
      });

      setMessage({
        type: 'success',
        text: `User ${alias} has been ${isGlobal ? 'globally ' : ''}banned successfully`,
      });

      // Reset form
      setAlias('');
      setTopicId('');
      setReason('');
      setDuration('');
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to ban user',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Ban User</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Prevent a user from posting in a topic or globally across all topics
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="alias" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            User ID *
          </label>
          <input
            type="text"
            id="alias"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            placeholder="Enter user UUID"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isGlobal"
            checked={isGlobal}
            onChange={(e) => setIsGlobal(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
          <label htmlFor="isGlobal" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Global Ban (applies to all topics)
          </label>
        </div>

        {!isGlobal && (
          <div>
            <label htmlFor="topicId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Topic ID {!isGlobal && '(optional for global ban)'}
            </label>
            <input
              type="text"
              id="topicId"
              value={topicId}
              onChange={(e) => setTopicId(e.target.value)}
              placeholder="Leave empty for global ban"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}

        <div>
          <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Reason *
          </label>
          <textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this user is being banned"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Duration (minutes)
          </label>
          <input
            type="number"
            id="duration"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="Leave empty for permanent ban"
            min="1"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Common: 60 (1 hour), 1440 (1 day), 10080 (1 week), 43200 (30 days)
          </p>
        </div>

        {message && (
          <div
            className={`p-3 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
            }`}
          >
            {message.text}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
        >
          {isSubmitting ? 'Banning User...' : 'Ban User'}
        </button>
      </form>
    </div>
  );
}
